import EmptyState from './EmptyState';

const DataTable = ({
  columns,
  rows,
  getRowKey = (row, index) => row._id || index,
  isLoading = false,
  isFetching = false,
  isError = false,
  error,
  emptyTitle = 'No records',
  emptyMessage = 'There are no records to display.',
  emptyIcon = null,
  pagination,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center rounded-2xl border border-cream/60 bg-white py-12 text-primary">
        Loading…
      </div>
    );
  }

  if (isError) {
    const message = error?.response?.data?.message || error?.message || 'Unable to load data.';
    return <EmptyState title="Action required" description={message} icon={emptyIcon} />;
  }

  if (!rows || rows.length === 0) {
    return <EmptyState title={emptyTitle} description={emptyMessage} icon={emptyIcon} />;
  }

  return (
    <div className="rounded-2xl border border-cream/60 bg-white">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-left text-sm text-slate-700">
          <thead className="bg-cream/60 text-xs uppercase tracking-[0.2em] text-primary">
            <tr>
              {columns.map((col) => (
                <th key={col.key || col.header} className={`px-4 py-3 ${col.headerClassName || ''} ${col.align === 'right' ? 'text-right' : ''}`}>
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                key={getRowKey(row, index)}
                className={`border-b border-slate-200 last:border-0 ${index % 2 === 0 ? 'bg-white' : 'bg-cream/90'}`}
              >
                {columns.map((col) => (
                  <td key={col.key || col.header} className={`px-4 py-3 ${col.className || ''} ${col.align === 'right' ? 'text-right' : ''}`}>
                    {typeof col.render === 'function' ? col.render(row) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {isFetching ? (
        <div className="border-t border-cream/60 bg-cream/40 px-4 py-2 text-xs text-slate-500">Refreshing…</div>
      ) : null}
      {pagination ? (
        <div className="flex flex-col items-center justify-between gap-3 border-t border-cream/60 px-4 py-3 sm:flex-row">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span>
              Page {pagination.currentPage} of {pagination.totalPages || 1}
              {typeof pagination.totalItems === 'number' ? ` · ${pagination.totalItems} total` : ''}
            </span>
            {pagination.onPageSizeChange ? (
              <select
                value={pagination.pageSize}
                onChange={(e) => pagination.onPageSizeChange(Number(e.target.value))}
                className="rounded-md border border-cream/60 bg-white px-2 py-1 text-xs text-slate-700 outline-none focus:border-primary"
              >
                {[10, 20, 50].map((size) => (
                  <option key={size} value={size}>
                    {size} / page
                  </option>
                ))}
              </select>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={!pagination.hasPrevPage}
              onClick={() => pagination.onPageChange(pagination.currentPage - 1)}
              className="rounded-md border border-cream/60 bg-white px-3 py-1.5 text-xs font-semibold text-primary transition hover:bg-cream/60 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={!pagination.hasNextPage}
              onClick={() => pagination.onPageChange(pagination.currentPage + 1)}
              className="rounded-md border border-cream/60 bg-white px-3 py-1.5 text-xs font-semibold text-primary transition hover:bg-cream/60 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DataTable;

